"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const EtudeError_1 = require("../error/EtudeError");
const EtudeParser_1 = require("../util/EtudeParser");
const Exceptional_1 = require("../util/Exceptional");
class Value {
    constructor(type, dotCount = 0, duration = type.getBaseDuration() * Math.pow(1.5, dotCount)) {
        this.type = type;
        this.dotCount = dotCount;
        this.duration = duration;
    }
    dotted() {
        return new Value(this.type, this.dotCount + 1, this.duration * 1.5);
    }
    undotted() {
        return new Value(this.type, this.dotCount - 1, this.duration / 1.5);
    }
    tuplet(n) {
        if (this.type === Value.Type.TWO_HUNDRED_FIFTY_SIXTH) {
            throw EtudeError_1.default.forIllegalState(Value, "etude does not support value types smaller than " + this.type);
        }
        return new Value(Value.Type.values()[this.type.ordinal() + 1], 0, this.duration / n);
    }
    triplet() {
        return this.tuplet(3);
    }
    static fromString(valueString) {
        return EtudeParser_1.default
            .of(valueString)
            .filter(o => o != null, EtudeError_1.default.forNull(Value))
            .parse(s => {
            // longest prefix that contains only letters or .
            const prefix = valueString.match("^[a-zA-Z.]+")[0];
            const dotIndex = prefix.indexOf('.');
            let typeString;
            let dotCount;
            if (dotIndex === -1) {
                typeString = prefix;
                dotCount = 0;
            }
            else {
                typeString = prefix.substring(0, dotIndex);
                dotCount = prefix.length - dotIndex;
            }
            const type = Value.Type.fromString(typeString);
            if (!type.isPresent()) {
                return Exceptional_1.default.empty();
            }
            // a number that has an open parentheses somewhere before it
            const durationString = valueString.match(/\([0-9.]+/g)[0];
            const duration = durationString != null
                ? parseFloat(durationString.substring(1))
                : type.get().getBaseDuration() * Math.pow(1.5, dotCount);
            return Exceptional_1.default.of(new Value(type.get(), dotCount, duration));
        })
            .get(a => a[0]);
    }
    toString() {
        return `${this.type}${".".repeat(this.dotCount)}(${this.duration})`;
    }
    getType() {
        return this.type;
    }
    getDotCount() {
        return this.dotCount;
    }
    getDuration() {
        return this.duration;
    }
}
(function (Value) {
    class Type {
        constructor() {
            ++Type.size;
            Type._values.push(this);
        }
        static values() {
            return Type._values.slice();
        }
        ordinal() {
            return Type._values.indexOf(this);
        }
        static valueOf(typeString) {
            const type = Type[typeString];
            if (type instanceof Type) {
                return type;
            }
            throw EtudeError_1.default.forInvalid(Type, typeString);
        }
        toString() {
            return Object.keys(Type).find(t => Type[t] === this);
        }
        static fromBaseDuration(baseDuration) {
            const ordinal = 1 - Math.log(baseDuration) / Math.log(2);
            if (ordinal % 1 !== 0) {
                throw EtudeError_1.default.forInvalid(Type, baseDuration, "should be a power of 2");
            }
            // n | 0 truncates to int
            return Type.values()[ordinal | 0];
        }
        static fromString(typeString) {
            return EtudeParser_1.default
                .of(typeString)
                .filter(o => o != null, EtudeError_1.default.forNull(Type))
                .parse(s => {
                const value = Type
                    .values()
                    .find(t => s.localeCompare(t.toString()) === 0);
                return Exceptional_1.default
                    .ofNullable(value)
                    .withException(EtudeError_1.default.forInvalid(Type, s));
            })
                .get(a => a[0]);
        }
        getBaseDuration() {
            return Math.pow(2, 1 - this.ordinal());
        }
    }
    Type.size = 0;
    Type._values = [];
    Type.DOUBLE_WHOLE = new Type();
    Type.WHOLE = new Type();
    Type.HALF = new Type();
    Type.QUARTER = new Type();
    Type.EIGHTH = new Type();
    Type.SIXTEENTH = new Type();
    Type.THIRTY_SECOND = new Type();
    Type.SIXTY_FOURTH = new Type();
    Type.HUNDRED_TWENTY_EIGHTH = new Type();
    Type.TWO_HUNDRED_FIFTY_SIXTH = new Type();
    Value.Type = Type;
    Value.DOUBLE_WHOLE = new Value(Type.DOUBLE_WHOLE);
    Value.WHOLE = new Value(Type.WHOLE);
    Value.HALF = new Value(Type.HALF);
    Value.QUARTER = new Value(Type.QUARTER);
    Value.EIGHTH = new Value(Type.EIGHTH);
    Value.SIXTEENTH = new Value(Type.SIXTEENTH);
    Value.THIRTY_SECOND = new Value(Type.THIRTY_SECOND);
    Value.SIXTY_FOURTH = new Value(Type.SIXTY_FOURTH);
    Value.HUNDRED_TWENTY_EIGHTH = new Value(Type.HUNDRED_TWENTY_EIGHTH);
    Value.TWO_HUNDRED_FIFTY_SIXTH = new Value(Type.TWO_HUNDRED_FIFTY_SIXTH);
})(Value || (Value = {}));
exports.default = Value;
